import React, { useState, useEffect } from 'react';
import authService from '../../api/auth.service';
import toast from 'react-hot-toast';

export const SignupForm = ({ onShowLogin }) => {
  const [signupMethod, setSignupMethod] = useState('email');
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [timeLeft, setTimeLeft] = useState(600);

  useEffect(() => {
    let timer;
    if (showOTPVerification && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [showOTPVerification, timeLeft]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const validateMobileNumber = (number) => /^\d{10}$/.test(number);
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleResendOTP = async () => {
    if (timeLeft > 0) return;

    setLoading(true);
    try {
      const response = await authService.mobileSignup({
        username: formData.username,
        phone_no: formData.mobileNumber,
        password: formData.password,
      });

      if (response.success) {
        toast.dismiss();
        toast.success('OTP sent successfully!');
        setTimeLeft(600);
      } else {
        throw new Error(response.message || 'Failed to send OTP');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      toast.dismiss();
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.verifyOTP({
        phone_no: formData.mobileNumber,
        otp,
        password: formData.password,
      });
      console.log('response: ', response);

      if (response.success) {
        toast.dismiss();
        toast.success('Phone number verified successfully!');
        onShowLogin();
      } else {
        throw new Error(response.message || 'Invalid OTP');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      toast.dismiss();
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    if (!formData.username.trim()) {
      setError('Username is required');
      toast.dismiss();
      toast.error('Username is required');
      return;
    }

    if (formData.username.length < 5) {
      setError('Username must be at least 5 characters long');
      toast.dismiss();
      toast.error('Username must be at least 5 characters long');
      return;
    }

    if (signupMethod === 'email') {
      if (!formData.email.trim()) {
        setError('Email is required');
        toast.dismiss();
        toast.error('Email is required');
        return;
      }
      if (!validateEmail(formData.email)) {
        setError('Please enter a valid email address');
        toast.dismiss();
        toast.error('Please enter a valid email address');
        return;
      }
    }

    if (signupMethod === 'mobile') {
      if (!formData.mobileNumber.trim()) {
        setError('Mobile number is required');
        toast.dismiss();
        toast.error('Mobile number is required');
        return;
      }
      if (!validateMobileNumber(formData.mobileNumber)) {
        setError('Please enter a valid 10-digit mobile number');
        toast.dismiss();
        toast.error('Please enter a valid 10-digit mobile number');
        return;
      }
    }

    if (!formData.password.trim()) {
      setError('Password is required');
      toast.dismiss();
      toast.error('Password is required');
      return;
    }

    if (!formData.confirmPassword.trim()) {
      setError('Confirm Password is required');
      toast.dismiss();
      toast.error('Confirm Password is required');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords don't match");
      toast.dismiss();
      toast.error("Passwords don't match");
      return;
    }

    setLoading(true);

    try {
      if (signupMethod === 'mobile') {
        const response = await authService.mobileSignup({
          username: formData.username,
          phone_no: formData.mobileNumber,
          password: formData.password,
        });

        if (response.success) {
          toast.dismiss();
          toast.success('OTP sent successfully!');
          setShowOTPVerification(true);
        } else {
          throw new Error(response.message || 'Failed to send OTP');
        }
      } else {
        const response = await authService.register({
          username: formData.username,
          email: formData.email,
          password: formData.password,
        });

        if (response.success) {
          setSuccessMessage(
            'Account created successfully! Please verify your email.'
          );
          toast.dismiss();
          toast.success(
            'Account created successfully! Please verify your email.'
          );
          setTimeout(() => onShowLogin(), 2000);
        } else {
          throw new Error(response.message || 'Registration failed');
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;
      setError(errorMessage);
      toast.dismiss();
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (showOTPVerification) {
    return (
      <div className='text-center'>
        <h2 className='text-2xl font-bold mb-2'>Verify OTP</h2>
        <p className='text-gray-600 mb-6'>
          Enter the OTP sent to {formData.mobileNumber}
        </p>

        <form onSubmit={handleOTPSubmit} className='space-y-4'>
          <div className='text-left'>
            <label className='block text-sm font-medium mb-1'>Enter OTP</label>
            <input
              type='text'
              placeholder='Enter 6-digit OTP'
              className='w-full p-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-textprimary focus:outline-none'
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setOtp(value);
                setError(null);
              }}
              maxLength={6}
              disabled={loading}
            />
          </div>

          <div className='text-center text-sm'>
            {timeLeft > 0 ? (
              <p className='text-gray-600'>
                Resend OTP in {formatTime(timeLeft)}
              </p>
            ) : (
              <button
                type='button'
                onClick={handleResendOTP}
                className='text-red-500 hover:text-red-600'
                disabled={loading}
              >
                Resend OTP
              </button>
            )}
          </div>

          <button
            type='submit'
            className='w-full bg-buttonprimary text-white py-2 rounded-xl hover:bg-buttonhoverPrimary transition-colors disabled:opacity-50'
            disabled={loading}
          >
            {loading ? (
              <span className='flex items-center justify-center'>
                <svg className='animate-spin h-5 w-5 mr-3' viewBox='0 0 24 24'>
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  />
                </svg>
                Verifying...
              </span>
            ) : (
              'Verify OTP'
            )}
          </button>

          <button
            type='button'
            onClick={() => {
              setShowOTPVerification(false);
              setError(null);
            }}
            className='w-full text-gray-600 hover:text-textDarkPrimary text-sm'
            disabled={loading}
          >
            Back to Signup
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className='text-center'>
      <h2 className='text-2xl text-textprimary font-bold mb-2'>
        Create an account
      </h2>
      <p className='text-gray-600 mb-6'>Connect with your friends today!</p>

      <div className='flex justify-center mb-6'>
        <button
          onClick={() => {
            setSignupMethod((prev) => (prev === 'email' ? 'mobile' : 'email'));
            setError(null);
            setSuccessMessage(null);
            setFormData((prev) => ({
              username: prev.username,
              email: '',
              mobileNumber: '',
              password: '',
              confirmPassword: '',
            }));
          }}
          className='text-sm text-textprimary hover:text-textprimary/80 underline'
          disabled={loading}
        >
          Sign up with {signupMethod === 'email' ? 'mobile number' : 'email'}{' '}
          instead
        </button>
      </div>

      {/* {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-500 rounded-lg text-sm">
                    {error}
                </div>
            )} */}

      {/* {successMessage && (
                <div className="mb-4 p-3 bg-green-50 text-green-500 rounded-lg text-sm">
                    {successMessage}
                </div>
            )} */}

      <form onSubmit={handleSignupSubmit} className='space-y-4'>
        <div className='text-left'>
          <label className='block text-sm font-medium mb-1'>Name</label>
          <input
            type='text'
            placeholder='Enter Username'
            className='w-full p-2 rounded-xl font-normal border-2 border-[#A9BAD4] focus:ring-2 focus:ring-textprimary focus:outline-none'
            value={formData.username}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, username: e.target.value }));
              setError(null);
            }}
            disabled={loading}
          />
        </div>

        {signupMethod === 'email' ? (
          <div className='text-left'>
            <label className='block text-sm font-medium mb-1'>
              Email Address
            </label>
            <input
              type='email'
              placeholder='Enter email'
              className='w-full p-2 rounded-xl font-normal border-2 border-[#A9BAD4] focus:ring-2 focus:ring-textprimary focus:outline-none'
              value={formData.email}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, email: e.target.value }));
                setError(null);
              }}
              disabled={loading}
            />
          </div>
        ) : (
          <div className='text-left'>
            <label className='block text-sm font-medium mb-1'>
              Mobile Number
            </label>
            <input
              type='tel'
              placeholder='Enter 10-digit mobile number'
              className='w-full p-2 rounded-xl font-normal border-2 border-[#A9BAD4] focus:ring-2 focus:ring-textprimary focus:outline-none'
              value={formData.mobileNumber}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                setFormData((prev) => ({ ...prev, mobileNumber: value }));
                setError(null);
              }}
              disabled={loading}
              maxLength={10}
            />
          </div>
        )}

        <div className='text-left'>
          <label className='block text-sm font-medium mb-1'>Password</label>
          <input
            type='password'
            placeholder='Enter password'
            className='w-full p-2 rounded-xl font-normal border-2 border-[#A9BAD4] focus:ring-2 focus:ring-textprimary focus:outline-none'
            value={formData.password}
            onChange={(e) => {
              setFormData((prev) => ({ ...prev, password: e.target.value }));
              setError(null);
            }}
            disabled={loading}
          />
        </div>

        <div className='text-left'>
          <label className='block text-sm font-medium mb-1'>
            Confirm Password
          </label>
          <input
            type='password'
            placeholder='Re-enter password'
            className='w-full p-2 rounded-xl font-normal border-2 border-[#A9BAD4] focus:ring-2 focus:ring-textprimary focus:outline-none'
            value={formData.confirmPassword}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                confirmPassword: e.target.value,
              }));
              setError(null);
            }}
            disabled={loading}
          />
        </div>

        <button
          type='submit'
          className='w-full bg-buttonprimary text-white py-2 rounded-xl hover:bg-buttonhoverPrimary transition-colors disabled:opacity-50'
          disabled={loading}
        >
          {loading ? (
            <span className='flex items-center justify-center'>
              <svg className='animate-spin h-5 w-5 mr-3' viewBox='0 0 24 24'>
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'
                />
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                />
              </svg>
              {signupMethod === 'mobile' ? 'Sending OTP...' : 'Signing up...'}
            </span>
          ) : signupMethod === 'mobile' ? (
            'Get OTP'
          ) : (
            'Sign up'
          )}
        </button>
      </form>

      <p className='mt-4 text-sm text-gray-600'>
        Already have an account?{' '}
        <button
          onClick={onShowLogin}
          className='text-textprimary underline hover:text-text-textprimary/80'
          disabled={loading}
        >
          Login
        </button>
      </p>
    </div>
  );
};

export default SignupForm;
